import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/lib/api";

export default function ContactFormModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ name: "", email: "", message: "" });
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#121212] border border-[#D4AF37]/30 max-w-md" data-testid="contact-form-modal">
        {!success ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-tight uppercase text-white">Get In Touch</DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm">
                Send us a message and one of our team will be in touch within 48 hours.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="contact-name-input"
              />
              <Input
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                data-testid="contact-email-input"
              />
              <textarea
                placeholder="Your message..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full bg-black border border-white/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none text-white placeholder:text-zinc-600 px-3 py-3 rounded-sm text-sm"
                data-testid="contact-message-input"
              />
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                data-testid="contact-submit-button"
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-8 space-y-4" data-testid="contact-success">
            <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Message Sent</h3>
            <p className="text-zinc-400 text-sm">
              Thank you for reaching out. One of our team will be in touch within 48 hours.
            </p>
            <Button
              onClick={handleClose}
              className="btn-gold h-10 px-6 rounded-sm text-xs tracking-[0.15em] uppercase"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
